import React from 'react';
import { useTranslation } from 'react-i18next';
import './ApprovalCard.css';

/**
 * Approval Card Component
 * Displays a single AI task awaiting approval
 */
function ApprovalCard({ task, onReview, isHighlighted }) {
  const { t } = useTranslation('attorney');

  // Normalize success likelihood to 0-100 scale
  const getSuccessPercentage = () => {
    const value = task.successLikelihood ?? task.result?.successLikelihood;
    if (value === undefined || value === null) return null;
    // If value is > 1, it's already 0-100 scale; otherwise multiply by 100
    return value > 1 ? Math.round(value) : Math.round(value * 100);
  };

  // Build a proper title from correspondence/task data
  const getTaskTitle = () => {
    const classification = task.input?.classification?.subType || task.input?.classification?.classification;
    const ipRef = task.correspondence?.ipOfficeRef || task.input?.correspondence?.ipOfficeRef;
    const trademarkName = task.trademarkName || task.input?.correspondence?.trademarkName;

    // Build title like "Adverse Report - TM2025/00147 - HEALTHEX"
    let title = '';
    if (classification === 'adverse_examination') {
      title = 'Adverse Report';
    } else if (classification === 'office_action') {
      title = 'Office Action';
    } else if (classification === 'opposition') {
      title = 'Opposition';
    } else {
      title = classification || 'Response Required';
    }

    if (ipRef) {
      title += ` - ${ipRef}`;
    }
    if (trademarkName) {
      title += ` - ${trademarkName}`;
    }

    return title;
  };

  const getTypeDisplay = () => {
    const classification = task.input?.classification?.subType || task.input?.classification?.classification;

    switch (classification) {
      case 'adverse_examination':
      case 'office_action':
        return {
          icon: '📝',
          label: t('attorney.approvalCard.officeAction', 'Office Action Response'),
          className: 'type-office-action'
        };
      case 'opposition':
        return {
          icon: '⚔️',
          label: t('attorney.approvalCard.opposition', 'Opposition Response'),
          className: 'type-opposition'
        };
      case 'renewal':
        return {
          icon: '🔄',
          label: t('attorney.approvalCard.renewal', 'Renewal Application'),
          className: 'type-renewal'
        };
      default:
        return {
          icon: '📋',
          label: task.taskType || classification || 'Task',
          className: 'type-default'
        };
    }
  };

  const getPriorityDisplay = () => {
    switch (task.priority) {
      case 'urgent':
        return { label: t('attorney.approvalCard.urgent', 'Urgent'), className: 'priority-urgent' };
      case 'high':
        return { label: t('attorney.approvalCard.high', 'High'), className: 'priority-high' };
      case 'medium':
        return { label: t('attorney.approvalCard.medium', 'Medium'), className: 'priority-medium' };
      default:
        return { label: t('attorney.approvalCard.low', 'Low'), className: 'priority-low' };
    }
  };

  const getStatusDisplay = () => {
    switch (task.status) {
      case 'approved':
        return { label: t('attorney.approvalCard.approved', 'Approved'), className: 'status-approved', icon: '✓' };
      case 'rejected':
        return { label: t('attorney.approvalCard.rejected', 'Rejected'), className: 'status-rejected', icon: '✕' };
      case 'awaiting_approval':
      default:
        return { label: t('attorney.approvalCard.pending', 'Pending'), className: 'status-pending', icon: '⏳' };
    }
  };

  const isCompleted = task.status === 'approved' || task.status === 'rejected';

  const type = getTypeDisplay();
  const priority = getPriorityDisplay();
  const status = getStatusDisplay();

  // Format deadline
  const deadlineDate = task.deadline ? new Date(task.deadline) : null;
  const daysUntilDeadline = deadlineDate
    ? Math.ceil((deadlineDate - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className={`approval-card ${priority.className} ${isHighlighted ? 'approval-card-highlighted' : ''}`}>
      <div className="approval-card-header">
        <div className="approval-card-type">
          <span className="type-icon">{type.icon}</span>
          <span className={`type-label ${type.className}`}>{type.label}</span>
        </div>
        <div className="approval-card-badges">
          {isCompleted && (
            <span className={`approval-card-status ${status.className}`}>
              {status.icon} {status.label}
            </span>
          )}
          <span className={`approval-card-priority ${priority.className}`}>
            {priority.label}
          </span>
        </div>
      </div>

      <div className="approval-card-body">
        <div className="approval-card-info">
          <span className="approval-card-title">{getTaskTitle()}</span>
          <span className="approval-card-app-id">{task.applicationId}</span>
        </div>

        <p className="approval-card-summary">
          {task.summary || task.result?.summary || task.context?.issueDescription || t('attorney.approvalCard.noSummary', 'No summary available')}
        </p>

        {getSuccessPercentage() !== null && (
          <div className="approval-card-confidence">
            <span className="confidence-label">
              {t('attorney.approvalCard.successLikelihood', 'Success Likelihood')}:
            </span>
            <span className="confidence-value">
              {getSuccessPercentage()}%
            </span>
          </div>
        )}
      </div>

      <div className="approval-card-footer">
        <div className="approval-card-meta">
          <span className="approval-card-client">
            {task.clientName || 'Unknown Client'}
          </span>
          {deadlineDate && (
            <span className={`approval-card-deadline ${daysUntilDeadline <= 7 ? 'deadline-urgent' : ''}`}>
              {daysUntilDeadline > 0
                ? t('attorney.approvalCard.daysLeft', '{{days}} days left', { days: daysUntilDeadline })
                : t('attorney.approvalCard.overdue', 'Overdue!')}
            </span>
          )}
        </div>

        <button className={`btn-review ${isCompleted ? 'btn-view' : ''}`} onClick={() => onReview(task)}>
          {isCompleted
            ? t('attorney.approvalCard.viewDetails', 'View Details')
            : t('attorney.approvalCard.review', 'Review Draft')
          }
        </button>
      </div>
    </div>
  );
}

export default ApprovalCard;
